import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import type { ProactiveGreetingQuickReply } from '@teo/widget-shared';
import { useWidget } from '../context/WidgetContext.js';
import { Avatar } from './Avatar.js';
import { IconClose } from './icons.js';
import { QuickReplyButtons } from './QuickReplyButtons.js';

type ContextPopupProps = {
  message: string;
  ctaText?: string;
  quickReplies?: ProactiveGreetingQuickReply[];
  showCta?: boolean;
  onOpenChat: () => void;
  onQuickReply?: (quickReply: ProactiveGreetingQuickReply) => void;
  onClose: () => void;
  /** When set (mobile drag/edge), popup is fixed-positioned to stay on-screen. */
  placementStyle?: CSSProperties;
};

export function ContextPopup({
  message,
  ctaText,
  quickReplies,
  showCta = true,
  onOpenChat,
  onQuickReply,
  onClose,
  placementStyle,
}: ContextPopupProps) {
  const { t, settings } = useWidget();
  const hasQuickReplies = Boolean(quickReplies?.length && onQuickReply);
  const quickRepliesRef = useRef<HTMLDivElement | null>(null);
  const [popupWidthPx, setPopupWidthPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!hasQuickReplies) {
      setPopupWidthPx(null);
      return;
    }

    const measure = () => {
      const buttonsWrap = quickRepliesRef.current;
      if (!buttonsWrap || typeof window === 'undefined') return;
      const desktopMaxWidth = Math.min(window.innerWidth - 32, 520);
      const nextWidth = Math.max(300, Math.min(desktopMaxWidth, buttonsWrap.scrollWidth + 24));
      setPopupWidthPx(nextWidth);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [hasQuickReplies, quickReplies]);

  return (
    <div
      className="teo-popup-wrap"
      style={placementStyle}
      data-teo-popup-anchored={placementStyle ? '1' : undefined}
    >
      <div
        className={`teo-popup${hasQuickReplies ? ' teo-popup--quick-replies' : ''}`}
        role="dialog"
        aria-live="polite"
        style={popupWidthPx ? { width: `${popupWidthPx}px` } : undefined}
      >
        <div className="teo-popup__head">
          <Avatar size="sm" />
          <span className="teo-popup__agent">{settings.content.aiAgentName}</span>
          <button
            type="button"
            className="teo-popup__close teo-icon-btn"
            onClick={() => onClose()}
            aria-label={t.close}
          >
            <IconClose size={16} />
          </button>
        </div>
        <p className="teo-popup__text">{message}</p>
        {hasQuickReplies ? (
          <div ref={quickRepliesRef}>
            <QuickReplyButtons
              quickReplies={quickReplies!}
              onSelect={onQuickReply!}
              variant="popup"
            />
          </div>
        ) : showCta ? (
          <button type="button" className="teo-popup__cta" onClick={() => onOpenChat()}>
            {ctaText?.trim() || t.popupCta}
          </button>
        ) : null
        }
      </div>
    </div>
  );
}
